import { useState, useEffect, useCallback } from "react";
import { useAuth } from "@/App";
import { Gavel, Plus, Clock, AlertTriangle, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function CourtCases() {
  const { API } = useAuth();
  const [cases, setCases] = useState([]);
  const [properties, setProperties] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ property_id: "", case_number: "", court_name: "", case_type: "civil", status: "active", description: "" });

  const fetchData = useCallback(async () => {
    try {
      const [cRes, pRes] = await Promise.all([
        fetch(`${API}/court-cases`, { credentials: "include" }),
        fetch(`${API}/properties`, { credentials: "include" }),
      ]);
      if (cRes.ok) setCases(await cRes.json());
      if (pRes.ok) setProperties(await pRes.json());
    } catch (e) { console.error(e); }
  }, [API]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async () => {
    try {
      const res = await fetch(`${API}/court-cases`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(form),
      });
      if (res.ok) { setShowForm(false); setForm({ property_id: "", case_number: "", court_name: "", case_type: "civil", status: "active", description: "" }); fetchData(); }
    } catch (e) { console.error(e); }
  };

  const getPropertyName = (id) => properties.find((p) => p.property_id === id)?.name || id;

  const statusConfig = {
    active: { icon: AlertTriangle, color: "text-[#F59E0B]", bg: "bg-[#F59E0B]/10", label: "Active" },
    resolved: { icon: CheckCircle, color: "text-[#556B2F]", bg: "bg-[#556B2F]/10", label: "Resolved" },
    pending: { icon: Clock, color: "text-[#D4AF37]", bg: "bg-[#D4AF37]/10", label: "Pending" },
  };

  return (
    <div data-testid="court-cases-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-serif text-2xl text-white">Court Case Tracking</h2>
          <p className="text-white/40 text-sm font-sans">{cases.length} cases registered</p>
        </div>
        <Button data-testid="add-court-case-btn" onClick={() => setShowForm(true)} className="bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">
          <Plus className="w-4 h-4 mr-2" /> Add Case
        </Button>
      </div>

      {cases.length === 0 ? (
        <div className="bg-[#11130F] border border-white/10 rounded-sm p-12 text-center">
          <Gavel className="w-12 h-12 text-white/10 mx-auto mb-4" />
          <p className="text-white/40 text-sm font-sans">No court cases tracked yet.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {cases.map((c) => {
            const st = statusConfig[c.status] || statusConfig.active;
            return (
              <div key={c.case_id} className="bg-[#11130F] border border-white/10 rounded-sm p-5 hover:border-[#D4AF37]/40 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3">
                    <Gavel className="w-5 h-5 text-[#D4AF37] mt-0.5" />
                    <div>
                      <h4 className="font-sans font-medium text-white text-sm">Case #{c.case_number}</h4>
                      <p className="text-white/40 text-xs font-sans mt-0.5">{c.court_name} &middot; {c.case_type}</p>
                      <p className="text-white/40 text-xs font-sans">Property: {getPropertyName(c.property_id)}</p>
                      {c.description && <p className="text-white/50 text-xs font-sans mt-2">{c.description}</p>}
                    </div>
                  </div>
                  <span className={`${st.bg} ${st.color} text-xs px-2 py-0.5 rounded-sm font-sans`}>{st.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="bg-[#11130F] border border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-xl">Add Court Case</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Property</label>
              <Select value={form.property_id} onValueChange={(v) => setForm({ ...form, property_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue placeholder="Select property" /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  {properties.map((p) => (<SelectItem key={p.property_id} value={p.property_id}>{p.name}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Case Number</label>
                <Input data-testid="case-number-input" value={form.case_number} onChange={(e) => setForm({ ...form, case_number: e.target.value })} placeholder="e.g., CS/123/2024" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
              <div>
                <label className="text-white/50 text-xs font-sans mb-1 block">Court Name</label>
                <Input value={form.court_name} onChange={(e) => setForm({ ...form, court_name: e.target.value })} placeholder="e.g., District Court" className="bg-white/5 border-white/10 text-white rounded-sm" />
              </div>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Case Type</label>
              <Select value={form.case_type} onValueChange={(v) => setForm({ ...form, case_type: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#11130F] border-white/10">
                  <SelectItem value="civil">Civil</SelectItem>
                  <SelectItem value="criminal">Criminal</SelectItem>
                  <SelectItem value="revenue">Revenue</SelectItem>
                  <SelectItem value="title_dispute">Title Dispute</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-white/50 text-xs font-sans mb-1 block">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Brief description..." className="bg-white/5 border-white/10 text-white rounded-sm" />
            </div>
            <Button data-testid="submit-court-case-btn" onClick={handleSubmit} className="w-full bg-[#D4AF37] text-black hover:bg-[#F59E0B] rounded-sm font-sans">Add Case</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
